import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell as BellIcon } from 'lucide-react'
import { useNorseTerm } from '../modules/norseterm/NorseTermData'
import { useFlexPdAlerts } from '../modules/flexpd/Requests'

/**
 * The bell: what needs this person, in every tool they use, grouped by tool.
 * Only things to act on; each links straight to where the work is. In-app
 * only: email, when it is set up, says the same things.
 */
export function Bell() {
  const nt = useNorseTerm()
  const fpd = useFlexPdAlerts()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onClick = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [open])
  const groups = [
    { tool: 'Norse Term', alerts: nt.enabled ? nt.alerts : [] },
    { tool: 'Flex PD', alerts: fpd },
  ].filter((g) => g.alerts.length)
  const n = groups.reduce((t, g) => t + g.alerts.reduce((s, a) => s + a.n, 0), 0)
  return (
    <div className="acct" ref={wrap}>
      <button className="acct__btn bell" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((o) => !o)}>
        <BellIcon size={17} aria-hidden="true" />
        {n > 0 && <span className="bell__n" aria-hidden="true">{n > 99 ? '99+' : n}</span>}
        <span className="visually-hidden">{n ? `${n} things need you` : 'Nothing needs you right now'}</span>
      </button>
      {open && (
        <div className="acct__panel bell__panel">
          {groups.length === 0
            ? <><div className="acct__label" style={{ paddingTop: 'var(--s-1)' }}>Needs you</div><p className="acct__hint">Nothing right now. You&apos;re all caught up.</p></>
            : groups.map((g) => (
              <div key={g.tool}>
                <div className="acct__label" style={{ paddingTop: 'var(--s-1)' }}>{groups.length > 1 ? g.tool : `Needs you · ${g.tool}`}</div>
                <ul className="bell__list">
                  {g.alerts.map((a) => (
                    <li key={a.id}>
                      <Link to={a.to} className="acct__item bell__item" onClick={() => setOpen(false)}>
                        {a.n > 1 && <span className="work__badge">{a.n}</span>}
                        <span>{a.text}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
